import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInventoryStatusHook } from '@hooks/prediction';
import {
  ProvidersSection,
  PurchaseOrdersSection,
  ShipmentsSection,
} from '@page-components/catalog-details/timeline';
import {
  DatesBar,
  differenceInDays,
  getDarkColor,
} from '@page-components/timeline/common';
import { DateUtility } from '@utility/date';
import { StyledFormTitle } from '@elements/Title';
import { cn } from '@skupreme/shared/ui';

const HeightBar = 23;
const DayWidth = 30;

export function ChartRendererNoData(props) {
  const { loading = false } = props;
  return (
    <div className="sk:flex! sk:items-center! sk:justify-center! sk:pt-5!">
      <StyledFormTitle>
        {loading ? 'Loading...' : 'No Inventory Flow Data'}
      </StyledFormTitle>
    </div>
  );
}

export function ChartRenderer({
  data,
  activateVelocityWindow,
  hidePrediction = true,
  setPredictedPo = () => {},
  setPredictedShipment = () => {},
  showShipmentCheckbox = false,
  hidePOPrediction = false,
}) {
  const chartRef = useRef(null);
  const { checkEntry } = useInventoryStatusHook(true);
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);

  const [minPODate, setMinPODate] = useState(null);
  const [maxPODate, setMaxPODate] = useState(null);

  const [minShipmentDate, setMinShipmentDate] = useState(null);
  const [maxShipmentDate, setMaxShipmentDate] = useState(null);

  const [minProviderDate, setMinProviderDate] = useState(null);
  const [maxProviderDate, setMaxProviderDate] = useState(null);

  const [shipmentUserValue, setShipmentUserValue] = useState({});

  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const [activeRow, setHoverRow] = useState(null);

  const [staticColors, setStaticColors] = useState({});

  const mouseOnSlider = e => {
    setIsDown(true);
    if (!chartRef.current) return;
    // @ts-expect-error - TS2339: Property 'offsetLeft' does not exist on type 'never'.
    setStartX(e.pageX - chartRef.current.offsetLeft);
    // @ts-expect-error - TS2339: Property 'scrollLeft' does not exist on type 'never'.
    setScrollLeft(chartRef.current.scrollLeft);
  };

  const notSliding = () => {
    if (!chartRef.current) return;
    setIsDown(false);
  };

  const moveSlider = e => {
    if (!isDown || !chartRef.current) return;
    e.preventDefault();
    // @ts-expect-error - TS2339: Property 'offsetLeft' does not exist on type 'never'.
    const x = e.pageX - chartRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    // @ts-expect-error - TS2339: Property 'scrollLeft' does not exist on type 'never'.
    if (chartRef.current) chartRef.current.scrollLeft = scrollLeft - walk;
  };

  const minDate = useMemo(() => {
    const arr = [minPODate, minShipmentDate, minProviderDate];
    let min = minShipmentDate || minPODate || minProviderDate;

    arr.forEach(date => {
      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(min, date)) {
        min = date;
      }
    });

    if (min) {
      return min;
    }

    return new Date();
  }, [minPODate, minShipmentDate, minProviderDate]);

  const maxDate = useMemo(() => {
    const arr = [maxPODate, maxShipmentDate, maxProviderDate];
    let max = maxShipmentDate || maxPODate || maxProviderDate;

    arr.forEach(date => {
      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(date, max)) {
        max = date;
      }
    });

    if (!max) {
      // @ts-expect-error - TS2322: Type 'Date' is not assignable to type 'null'.
      max = new Date();
    }

    // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
    if (DateUtility.difference(minDate, max) < 25) {
      // @ts-expect-error - TS2322: Type 'Date' is not assignable to type 'null'.
      max = DateUtility.addDays(minDate, 25);
    }
    // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
    if (DateUtility.difference(new Date(), max) < 25) {
      // @ts-expect-error - TS2322: Type 'Date' is not assignable to type 'null'.
      max = DateUtility.addDays(new Date(), 25);
    }

    return max;
  }, [maxPODate, maxShipmentDate, maxProviderDate, minDate]);

  const differenceInDates = useMemo(() => {
    // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
    const diff = DateUtility.difference(minDate, maxDate);
    return diff < 30 ? 30 : diff;
  }, [maxDate, minDate]);

  useEffect(() => {
    if (minDate) {
      const isInPast = differenceInDays(minDate, new Date());
      if (isInPast > 0 && chartRef.current) {
        // @ts-expect-error - TS2339: Property 'scrollLeft' does not exist on type 'never'.
        chartRef.current.scrollLeft = isInPast * DayWidth;
      }
    }
  }, [minDate, data]);

  const getBarColor = useCallback(
    id => {
      if (staticColors[id]) {
        return staticColors[id];
      }

      const newColor = getDarkColor();
      if (id) {
        setStaticColors(colors => ({ ...colors, [id]: newColor }));
      }

      return newColor;
    },
    [staticColors],
  );

  const commonProps = {
    data,
    getBarColor,
    HeightBar,
    DayWidth,
    minDate,
    setHoverRow,
    activeRow,
    differenceInDates,
    checkEntry,
    shipmentUserValue,
    providers: filteredProviders,
    shipments: filteredShipments,
    purchaseOrders: filteredPurchaseOrders,
  };

  return (
    <div
      ref={chartRef}
      onMouseMove={moveSlider}
      onMouseLeave={notSliding}
      onMouseUp={notSliding}
      onMouseDown={mouseOnSlider}
      className={cn(
        'sk:relative sk:min-w-full sk:max-w-full sk:scroll-smooth sk:overflow-y-hidden sk:select-none sk:bg-surface sk:bg-[linear-gradient(var(--sk-fill-tertiary),var(--sk-fill-tertiary))]',
        isDown ? 'active sk:cursor-grabbing' : 'sk:cursor-grab',
      )}
    >
      <DatesBar startDate={minDate} endDate={maxDate} DayWidth={DayWidth} />

      <PurchaseOrdersSection
        setMinDate={setMinPODate}
        setMaxDate={setMaxPODate}
        filteredPurchaseOrders={filteredPurchaseOrders}
        setFilteredPurchaseOrders={setFilteredPurchaseOrders}
        setPredictedPo={setPredictedPo}
        hidePrediction={hidePrediction}
        {...commonProps}
      />

      <ShipmentsSection
        setMinDate={setMinShipmentDate}
        setMaxDate={setMaxShipmentDate}
        setChecked={setShipmentUserValue}
        checked={shipmentUserValue}
        setFilteredShipments={setFilteredShipments}
        filteredShipments={filteredShipments}
        setPredictedShipment={setPredictedShipment}
        showShipmentCheckbox={showShipmentCheckbox}
        hidePOPrediction={hidePOPrediction}
        {...commonProps}
      />
      <ProvidersSection
        setFilteredProviders={setFilteredProviders}
        filteredProviders={filteredProviders}
        setMinDate={setMinProviderDate}
        setMaxDate={setMaxProviderDate}
        maxDate={maxDate}
        activateVelocityWindow={activateVelocityWindow}
        hidePOPrediction={hidePOPrediction}
        {...commonProps}
      />
    </div>
  );
}
